/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, zip, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import * as actions from '@flight-results/store/actions/flights-results.actions';
import {
  FlightItem,
  FlightResultDesktop,
  FlightResultsMobile,
  FlightResultsMobileBySector,
  AirGroupResults,
  AirMobileResults,
  NewFlightItemVm
} from '@app/entities/flight-results';
import { getUiFlightsState, showedDetailsSelector } from '@flight-results/store/selectors/ui.selector';
import { UiFlightsState } from '@flight-results/store/state/flight-results-module.state';
import { flightResultsRootSelector } from '@flight-results/store/selectors/flight-results-root.selector';
import { FilterApplier } from '@shared/flight-itinerary/entities/filters';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import {
  NewAirSearchPayload,
  RequestSearchPayload,
  SearchFlightPayload,
  SearchPayload
} from '@flight-results/entities/request-search.payload';
import { resetFilters } from '@flight-results/mappers/filters.mapper';
import { SearchResultsData } from '@flight-results/entities/search-results-data';
import { flightKeysMapper, flightKeysMobileMapper } from '@flight-results/mappers/flight-keys.mapper';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { selectPassengerArrangement } from '@shared/base-search-bar/store/selectors/passenger-arrangement.selector';
import { PriceWidget, HourWidget } from '@app/entities/flight-results-widget';
import { FlexibleDatesPayload } from '@flight-results/entities/flexible-dates.payload';
import { FlexibleDatesResult, FlexibleDatesMappedResult } from '@flight-results/entities/flexible.dates';
import { exchangeSearchAction, exchangeBookAction } from '@user-history/store/actions/user-history.actions';
import { ExchangeBookPayload } from '@user-history/entities/user-history-item';
import { UpsellFacade } from '@upsell/upsell.facade';
import { SlackPushPayload } from '@notifications/entities/slack-push';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { GlobalFacade } from '@app/global.facade';
import { AirFilterResponseVm, AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { mapAirMultipleFilterPayload, mapAirFilterPayload } from '@app/mappers/global-filters/air-global-filters.mapper';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { STATSIG_EXPERIMENTS } from '@statsig/statsig.config';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Injectable({ providedIn: 'root' })
export class FlightResultsFacade {

  constructor(
    private readonly store: Store<State>,
    private readonly upsellFacade: UpsellFacade,
    private readonly globalFacade: GlobalFacade,
    private readonly statsigExperimentsFacade: StatsigExperimentsFacade
  ) { }

  public flightFilters$: Observable<AirFilterVm[]> = this.globalFacade.airFilterVm$();
  public filters$: Observable<AirFilterResponseVm> = this.globalFacade.airFilters$;

  public flightResultsDesktop$: Observable<FlightResultDesktop[]> = this.store
    .pipe(select(flightResultsRootSelector),
      map(state => (state?.flightResultsDesktop.Results as AirGroupResults).AirGroupedResults));

  public isLoadingFlights$: Observable<boolean> = this.globalFacade.ui.results.isLoadingResults$;
  public isAnInternationalTrip$: Observable<boolean> = this.store.pipe(map((state) => state.isAnInternationalSearch));
  public isLoadingExchange$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state?.isLoadingExchange));
  public errorInExchangeBook$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.errorInExchangeBook));
  public successExchange$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.successExchange));
  public isLoadingExchangePush$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.isLoadingExchangePush));
  public selectedItinerary$: Observable<string> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.itinerarySelected));
  public isLoadingScrollFlight$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState),
      map((state: UiFlightsState) => state.isLoadingFlightsScroll));
  public isLoadingFilters$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.isLoadingFilters));
  public isLoadingFlexDates$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.isLoadingFlexDates));
  public showedDetails$: Observable<string[]> = this.store.pipe(select(showedDetailsSelector));
  public errors$: Observable<ErrorHandle> = this.globalFacade.ui.errorHandle$;
  public errorInFlights$: Observable<boolean> = this.errors$.pipe(map((state) => state.status));
  public isClearFlights$: Observable<boolean> = this.store
    .pipe(
      select(getUiFlightsState),
      map((state: UiFlightsState) => state.isclearFlights)
    );
    
  public flightResultsMobile$: Observable<FlightResultsMobile[]> = this.store
    .pipe(select(flightResultsRootSelector),
      map(state => (state?.flightResultsMobile.Results as AirMobileResults).AirResults));

  public flightKeyMatched$: Observable<boolean> = this.store
    .pipe(select(flightResultsRootSelector),
      map(state => (state?.flightResultsMobile.Results as AirMobileResults).FlightKeyMatched));
    
  public flightKeyMatchedGroupResult$: Observable<boolean> = this.store
    .pipe(select(flightResultsRootSelector),
      map(state => (state?.flightResultsDesktop.Results as AirMobileResults).FlightKeyMatched));

  public flightResultsMobileBySector$: Observable<FlightResultsMobileBySector[]> = this.store
    .pipe(select(flightResultsRootSelector), map(state => state?.flightResultsMobileBySector || []));

  public searchResultsData$: Observable<SearchResultsData> = this.store
    .pipe(
      select(flightResultsRootSelector),
      map(state => state.searchResultsData)
    );
  public airSearchResultsPayload$: Observable<NewAirSearchPayload> = this.store
    .pipe(
      select(flightResultsRootSelector),
      map(state => state?.searchPayload)
    );
  public searchPayload$: Observable<SearchPayload> = this.airSearchResultsPayload$
    .pipe(
      map(payload => payload.Results)
    );
  public orderCriteria$: Observable<SorterTypes> = this.searchPayload$.pipe(
    map(payload => payload?.OrderCriteria)
  );
  public orderCriteriaSectorNumber$: Observable<number> = this.searchPayload$.pipe(
    map(payload => payload.OrderCriteriaSectorNumber)
  );
  public orderDesc$: Observable<boolean> = this.searchPayload$.pipe(
    map(payload => payload.OrderDesc)
  );
  public passengerArrangementsState$: Observable<PassengerArrangement[]> = this.store
    .pipe(select(selectPassengerArrangement));
  public flexDates$: Observable<FlexibleDatesResult> = this.store
    .pipe(select(flightResultsRootSelector), map(state => state.flexDates));
  public flexDatesMapped$: Observable<FlexibleDatesMappedResult> = this.store
    .pipe(
      select(flightResultsRootSelector),
      map(state => state.flexDatesMapped)
    );
  public disabledFlightScroll$: Observable<boolean> = this.store
    .pipe(select(getUiFlightsState), map((state: UiFlightsState) => state.disabledFlightScroll));

  public sortingHasBeenChanged$: Observable<boolean> = this.globalFacade.ui.results.filters.sortingHasBeenChanged$;

  public flightSortingHasBeenChanged(payload: boolean): void {
    this.globalFacade.ui.results.filters.sortingHasBeenChanged(payload);
  }

  public searchFlights(payload: SearchFlightPayload): void {
    this.store.dispatch(actions.searchFlightsAction({ payload: payload }));
  }
  
  public exchangeSearch(payload: RequestSearchPayload): void {
    let Trips = [];
    if (
      payload.Trips
        .some(trip => payload.exchangeDisabledSector
          .some(disabled => trip.SectorNumber === disabled.SectorNumber)
        )
    ) {
      Trips = payload.Trips;
    } else {
      Trips = payload.Trips.concat(payload.exchangeDisabledSector).sort((sectorA, sectorB) =>
        sectorA.SectorNumber - sectorB.SectorNumber);
    }
    this.store.dispatch(exchangeSearchAction({ payload: { ...payload, Trips } }));
  }
  
  public exchangeBook(
    payload: ExchangeBookPayload, selectedFlights: NewFlightItemVm[],
    freeOfCharge: boolean, isMobile: boolean, exchangeWithAssistance: boolean
  ): void {
    const flightKeys = isMobile ? flightKeysMobileMapper(selectedFlights) : flightKeysMapper(selectedFlights);
    this.store.dispatch(exchangeBookAction({ payload, flightKeys, freeOfCharge, exchangeWithAssistance }));
  }
  
  public searchflexDates(payload: FlexibleDatesPayload): void {
    this.store.dispatch(actions.searchFlexDatesAction({ payload: payload }));
  }

  public selectFlights(selectedFlights: NewFlightItemVm[]): void {
    const flightKeys = flightKeysMapper(selectedFlights);
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchPayload => {
      this.store.dispatch(actions.fetchFlightsOptionAction({
        payload: {
          SearchId: searchPayload.Results.SearchId,
          SessionToken: searchPayload.SessionToken,
          FlightKeys: flightKeys,
          AirlineCode: selectedFlights[0].AirlineCode
        }
      }));
    });
  }
  
  public selectFlightsForUpsell(selectedFlights: FlightItem[]): void {
    const flightKeys = flightKeysMapper(selectedFlights);
    combineLatest([this.airSearchResultsPayload$, this.flightUpsellTreatment$(SearchBy.FLIGHTS)]).pipe(take(1))
      .subscribe(([searchPayload, upsellTreatment]) => {
        this.upsellFacade.fetchOptionForUpsellAction({
          SearchId: searchPayload.Results.SearchId,
          SessionToken: searchPayload.SessionToken,
          FlightKeys: flightKeys,
          AirlineCode: selectedFlights[0].AirlineCode
        }, upsellTreatment);
      }).unsubscribe();
  }
  public clearFilterAndResults(isBySector: boolean): void {
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(airSearchResultsPayload => {
      this.store.dispatch(
        isBySector ?
          actions.fetchAirResultsBySectorAction({ payload: resetFilters(airSearchResultsPayload) }) :
          actions.fetchAirResultsAction({ payload: resetFilters(airSearchResultsPayload) })
      );
    }).unsubscribe();
  }

  public searchFlightsWithSingleFilter(filterApplier: FilterApplier[], isMobile: boolean): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile }));
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchResultsPayload => {
      const payload = mapAirMultipleFilterPayload(filterApplier, searchResultsPayload.Results) as SearchPayload;
      this.store.dispatch(isMobile ?
        actions.fetchAirResultsAction({ payload: {...searchResultsPayload, Results: payload} }) :
        actions.fetchAirGroupResultsAction({ payload: {...searchResultsPayload, Results: payload} }));
    }).unsubscribe();
  }

  public searchFlightsWithMultipleFilters(filterApplier: FilterApplier[], isMobile: boolean): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile }));
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchResultsPayload => {
      const payload = mapAirMultipleFilterPayload(filterApplier, searchResultsPayload.Results) as SearchPayload;
      this.store.dispatch(isMobile ?
        actions.fetchAirResultsAction({ payload: {...searchResultsPayload, Results: payload} }) :
        actions.fetchAirGroupResultsAction({ payload: {...searchResultsPayload, Results: payload} }));
    }).unsubscribe();
  }

  public searchFlightsWithSingleFilterBySector(filterApplier: FilterApplier): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile: true }));
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchResultsPayload => {
      const payload = mapAirFilterPayload(filterApplier, searchResultsPayload.Results) as SearchPayload;
      this.store.dispatch(actions.fetchAirResultsBySectorAction({ payload: {...searchResultsPayload, Results: payload} }));
    }).unsubscribe();
  }

  public searchFlightsWithMultipleFiltersBySector(filterApplier: FilterApplier[]): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile: true }));
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchResultsPayload => {
      const payload = mapAirMultipleFilterPayload(filterApplier, searchResultsPayload.Results) as SearchPayload;
      this.store.dispatch(actions.fetchAirResultsBySectorAction({ payload: {...searchResultsPayload, Results: payload} }));
    }).unsubscribe();
  }

  public resetSearchAfterTimeOut(isMobile: boolean, isBySector: boolean): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile }));
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchResultsPayload => {
      const payload = {
        ...searchResultsPayload.Results,
        ResultCountLowerBound: 0,
        ResultCountUpperBound: (isMobile ? 40 : 20),
      };
      if (isMobile) {
        if (isBySector) {
          this.store.dispatch(actions.fetchAirResultsBySectorAction({ payload: {...searchResultsPayload, Results: payload} }));
        } else {
          this.store.dispatch(actions.fetchAirResultsAction({ payload: {...searchResultsPayload, Results: payload} }));
        }
      } else {
        this.store.dispatch(actions.fetchAirGroupResultsAction({ payload: {...searchResultsPayload, Results: payload} }));
      }
    }).unsubscribe();
  }

  public searchFlightsWithPagination(isMobile: boolean): void {
    zip(
      this.airSearchResultsPayload$.pipe(take(1)),
      this.searchResultsData$.pipe(take(1))
    ).pipe(
      map(([searchResultsPayload, searchResultsResponse]) => {
        return { searchResultsPayload, searchResultsResponse };
      }),
      filter(({searchResultsPayload, searchResultsResponse}) => 
        searchResultsResponse.ResultsCount > searchResultsPayload.Results.ResultCountUpperBound)
    ).subscribe(({searchResultsPayload, searchResultsResponse}) => {
      const payload = {
        payload: {
          ...searchResultsPayload,
          Results: {
            ...searchResultsPayload.Results,
            ResultCountLowerBound: searchResultsPayload.Results.ResultCountUpperBound + 1,
            ResultCountUpperBound: searchResultsPayload.Results.ResultCountUpperBound + (isMobile ? 40 : 20),
          }
        },
        paginated: true
      };
      isMobile ?
        this.store.dispatch(actions.fetchAirResultsAction(payload)) :
        this.store.dispatch(actions.fetchAirGroupResultsAction(payload));
    }).unsubscribe();
  }
  
  public fetchUpsellOptionsFlights(
    optionId: number, isFlightUpsellTreatment?: boolean, isBySector?: boolean, previousOptions?: number[]): void {
    this.airSearchResultsPayload$.pipe(take(1)).subscribe(searchPayload => {
      isBySector ?
        this.upsellFacade.fetchUpsellOptionsFlightsBySector(optionId,
          searchPayload.Results?.SearchId,
          searchPayload.SessionToken,
          previousOptions
        ) :
        this.upsellFacade.fetchUpsellOptionsFlights(
          optionId,
          searchPayload.Results?.SearchId,
          searchPayload.SessionToken,
          isFlightUpsellTreatment
        );
    }).unsubscribe();
  }

  public fetchAirResultsBySectorAction(payload: NewAirSearchPayload): void {
    this.store.dispatch(actions.clearFlightResultsAction({ isMobile: true }));
    this.store.dispatch(actions.fetchAirResultsBySectorAction({
      payload: payload, fetchFilters: true
    }));
  }

  public setFlightResultsLoading(payload: boolean): void {
    this.store.dispatch(actions.setFlightResultsLoadingAction({ payload }));
  }

  get filteredAirlines$(): Observable<string[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.searchPayload.Results?.Airlines));
  }
  get filteredDepartureTimes$(): Observable<{}> {
    return this.store.pipe(select(state => state.flightResultsModuleState.searchPayload.Results.DepartureTimes));
  }
  get infoPriceWidgets$(): Observable<PriceWidget[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.flightResultsWidgets.PriceWidget));
  }
  get infoDepartureWidget$(): Observable<HourWidget[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.flightResultsWidgets.HourWidget));
  }
  get infoDepartureWidgetReturn$(): Observable<HourWidget[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.flightResultsWidgets.HourWidgetReturn));
  }
  get infoFlightDurationWidget$(): Observable<HourWidget[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.flightResultsWidgets.FlightDurationWidget));
  }
  get infoFlightDurationWidgetReturn$(): Observable<HourWidget[]> {
    return this.store.pipe(select(state => state.flightResultsModuleState.flightResultsWidgets.FlightDurationWidgetReturn));
  }
  public flightUpsellTreatment$(searchBy: SearchBy): Observable<boolean> {
    return this.statsigExperimentsFacade.experimentTreatmentOn$(STATSIG_EXPERIMENTS.FLIGHT_UPSELL)
      .pipe(map( treatment => 
        treatment && (searchBy === SearchBy.FLIGHTS || searchBy === SearchBy.FLIGHTS_BY_SECTOR)))
  }
  public clearFlightResultModuleState(): void {
    this.store.dispatch(actions.clearFilterAction());
    this.store.dispatch(actions.clearSearchPayloadAction());
    this.store.dispatch(actions.clearSearchResultDataAction());
  }
  public disabledFlightScroll(payload: boolean = true): void {
    this.store.dispatch(actions.disabledFlightScrollAction({ payload }));
  }
  public slackResidualExchangePush(payload: SlackPushPayload): void {
    this.store.dispatch(actions.slackResidualExchangePushAction({ payload }));
  }
  public setShowedDetails(payload: string): void {
    this.store.dispatch(actions.setShowedDetailsAction({ payload }));
  }
}
